import { useCallback, useEffect, useRef } from "react"
import { useBoundContext } from "lib/@components/binding/use-bound-context"

const DWELL_MS = 1000
const VISIBLE_RATIO = 0.5

// Marks a comment thread read once its row has been at least half visible for DWELL_MS — long enough
// that the reader has come to rest on it, so a fast scroll past does not count. Returns a ref callback
// to attach to the row's root element. No-ops when there is no read tracker in context or
// IntersectionObserver is unavailable (server render / old browser).
// One-shot per mount; the tracker itself ignores threads that are already read.
export function useMarkCommentThreadReadOnView(threadId) {
    const { markThreadSeen } = useBoundContext()
    const state = useRef({ done: false }).current
    state.threadId = threadId
    state.markThreadSeen = markThreadSeen

    useEffect(() => teardown, [])

    return useCallback(attach, [])

    function attach(element) {
        teardown()
        if (!element || state.done || !state.markThreadSeen || typeof IntersectionObserver === "undefined") return
        state.observer = new IntersectionObserver(onIntersect, { threshold: VISIBLE_RATIO })
        state.observer.observe(element)
    }

    function onIntersect(entries) {
        const visible = entries.some((entry) => entry.intersectionRatio >= VISIBLE_RATIO)
        if (visible && !state.timer) {
            state.timer = setTimeout(commit, DWELL_MS)
        } else if (!visible && state.timer) {
            clearTimeout(state.timer)
            state.timer = null
        }
    }

    function commit() {
        state.done = true
        teardown()
        state.markThreadSeen?.(state.threadId)
    }

    function teardown() {
        if (state.timer) {
            clearTimeout(state.timer)
            state.timer = null
        }
        if (state.observer) {
            state.observer.disconnect()
            state.observer = null
        }
    }
}
